import { useListSpaces } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { capitalize } from '@cocoonspace/shared/utils/capitalize'
import { track as vercelTrack } from '@vercel/analytics'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { SeoTags } from '~/components/seo-tags'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
	HomeBreadcrumbItem,
} from '~/components/ui/breadcrumb'
import { buttonVariants } from '~/components/ui/button'
import { SpaceCard } from '~/domains/search/components/space-card'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'

const IMG_REFS: Record<string, string> = {
	paris: '/images/v2/navbar/paris.svg',
	lyon: '/images/v2/navbar/lyon.svg',
}

export interface DistrictPageProps {
	filters: Partial<ListSpacesParams>
	districtZip: string
}

export const DistrictPage = ({ filters, districtZip }: DistrictPageProps) => {
	const { query } = useRouter()
	const { t, i18n } = useTranslation()
	const { track } = useTracking()

	const zone = query.zone as string

	const { data } = useListSpaces(filters)

	const spaceGroups = useMemo(() => {
		return data?.data.reduce<Record<string, Space[]>>((acc, space) => {
			const { address1, address2, postal_code, city } = space.address

			const address = `${address1 || ''} ${address2 || ''} ${
				postal_code || ''
			} ${city || ''}`

			acc[address] = acc[address] || []
			acc[address].push(space)

			return acc
		}, {})
	}, [data?.data])

	const jsonLD = useMemo(
		() => ({
			'@context': 'https://schema.org',
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: t('cwDistricts:metaTitle', { zone, districtZip }),
					item: `https://www.cocoon-space.com/salle-de-reunion/${zone}`,
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: t(`cwDistricts:details.${districtZip}.title`),
				},
			],
		}),
		[t, districtZip],
	)

	return (
		<div className='container flex flex-col items-stretch gap-8 py-8'>
			<SeoTags
				title={t('cwDistricts:metaTitle', { zone, districtZip })}
				description={t(`cwCommon:seo.metaDescription.${zone}`)}
			>
				<script
					dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
					type='application/ld+json'
				/>
			</SeoTags>

			<Breadcrumb>
				<BreadcrumbList>
					<HomeBreadcrumbItem
						onClick={() => {
							track.click('breadcrumb-link', { route: '/' })
							vercelTrack('click-breadcrumb-link', { target: '/' })
						}}
					/>

					<BreadcrumbSeparator />

					<BreadcrumbItem>
						<BreadcrumbLink
							href={`/${zone}`}
							className='capitalize'
							onClick={() => {
								track.click('breadcrumb-link', { route: `/${zone}` })
								vercelTrack('click-breadcrumb-link', { target: `/${zone}` })
							}}
						>
							{zone}
						</BreadcrumbLink>
					</BreadcrumbItem>

					<BreadcrumbSeparator />

					<BreadcrumbItem>
						<BreadcrumbPage className='capitalize'>
							{`${capitalize(zone)} ${t(
								`cwDistricts:details.${districtZip}.title`,
							)}`}
						</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<div className='flex flex-col items-start gap-4'>
				<header>
					{IMG_REFS[zone] && (
						<Image
							alt='Paris'
							src={IMG_REFS[zone]}
							height={60}
							width={60}
							className='mb-2'
						/>
					)}

					<h1 className='font-bold text-4xl'>
						{t('cwDistricts:pageHeader', { zone, districtZip })}
					</h1>

					<h2 className='max-w-[800px] text-lg'>{t('cwDistricts:baseline')}</h2>
				</header>

				{i18n.exists(`cwDistricts:details.${districtZip}.body`) && (
					<div className='max-w-[800px]'>
						{t(`cwDistricts:details.${districtZip}.body`)}
					</div>
				)}

				<Link
					href={`/${zone}`}
					className={cn(buttonVariants({ variant: 'gradient' }))}
					onClick={() => track.click('landing-district-seeAllCocoonBtn')}
				>
					{t('cwDistricts:seeAllCocoonInBtn', { zone })}
				</Link>
			</div>

			{!!spaceGroups && (
				<div className='flex flex-col items-stretch justify-start gap-y-8'>
					{Object.entries(spaceGroups).map(([address, spaces]) => (
						<div
							key={address}
							className='flex flex-col items-stretch justify-start gap-y-4'
						>
							<h2 className='max-w-[800px] font-semibold text-lg'>
								{t('cwDistricts:spaceGroupTitle', { address })}
							</h2>

							<div
								data-testid='venue-spaces-group'
								className='grid grid-cols-1 gap-4 pt-[2px] md:grid-cols-2 lg:grid-cols-3'
							>
								{spaces?.map((space) => (
									<SpaceCard
										key={space.id}
										space={space}
										href={`/${space.zone}/${space.slug}`}
										onClick={() => track.click('landing-district-spaceCard')}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
